<template>
  <div class="rnc-body">
    <!-- Role name -->
    <div class="rnc-body__field">
      <label class="rnc-body__label" :for="nameId">
        {{ resolvedNameLabel }}
      </label>
      <div class="rnc-body__input-wrap">
        <input
          :id="nameId"
          ref="nameInput"
          :value="name"
          class="rnc-body__input rnc-body__input--with-counter"
          :class="{ 'rnc-body__input--error': nameTooLong }"
          type="text"
          :placeholder="resolvedNamePlaceholder"
          :aria-invalid="nameTooLong || !!nameError ? 'true' : 'false'"
          :aria-describedby="nameError ? `${nameId}-error` : undefined"
          @focus="onNameFocus"
          @blur="onNameBlur"
          @input="onNameInput"
        />
        <span
          v-if="nameFocused || name.length > 0"
          class="rnc-body__counter"
          :class="{ 'rnc-body__counter--error': remainingChars < 0 }"
          aria-live="polite"
        >
          {{ remainingChars }}
        </span>
      </div>
      <p
        v-if="nameError"
        :id="`${nameId}-error`"
        class="rnc-body__error"
        role="alert"
      >
        {{ nameError }}
      </p>
    </div>

    <!-- Context + IQ Suggest (gated) -->
    <div v-if="flagEnabled" class="rnc-body__field">
      <div class="rnc-body__context-label-row">
        <label class="rnc-body__label" :for="contextId">
          Context
          <span class="rnc-body__optional">{{ contextOptionalLabel }}</span>
        </label>
        <div v-if="!isArchived" class="rnc-body__suggest">
          <AskIQButton
            :label="suggestLabel"
            variant="light"
            :height="40"
            :disabled="suggestDisabled"
            :hide-icon="isStreaming"
            @click="handleSuggestClick"
          />
          <template v-if="showReplacePopover">
            <div
              class="rnc-body__replace-backdrop"
              @click="setReplacePopover(false)"
            ></div>
            <div
              class="rnc-body__replace-popover"
              role="dialog"
              aria-label="Replace existing context?"
            >
              <p class="rnc-body__replace-title">Replace existing context?</p>
              <p class="rnc-body__replace-body">
                IQ will overwrite what is currently in Context. You can edit the new text before saving.
              </p>
              <div class="rnc-body__replace-actions">
                <button
                  type="button"
                  class="rnc-body__replace-btn rnc-body__replace-btn--cancel"
                  @click="setReplacePopover(false)"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="rnc-body__replace-btn rnc-body__replace-btn--confirm"
                  @click="handleReplace"
                >
                  Replace
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- IQThinkingBannerStreaming replaces the textarea while a suggestion runs
           (Connecting / Streaming / Still working / Done / Failed). -->
      <IQThinkingBanner
        v-if="showBanner"
        :state="bannerState"
        :title="CONNECTING_TITLE"
        :phase="phase"
        :reasoning="reasoning"
        :stalled="stalled"
        :collapsible="bannerCollapsible"
        :hide-retry="true"
        :use-store-reasoning="false"
        :failed-title="FAILED_TITLE"
        :failed-message="failedMessage"
      />

      <div
        v-if="showTextarea"
        class="rnc-body__textarea-wrapper"
        :class="{ 'rnc-body__textarea-wrapper--error': contextTooLong }"
      >
        <textarea
          :id="contextId"
          :value="context"
          class="rnc-body__textarea"
          maxlength="500"
          :disabled="isArchived"
          :placeholder="contextPlaceholder"
          aria-label="Context"
          @focus="onContextFocus"
          @blur="onContextBlur"
          @input="onContextInput"
        ></textarea>
      </div>

      <div v-if="showHintRow" class="rnc-body__context-hint-row">
        <p class="rnc-body__hint" aria-live="polite">{{ contextHint }}</p>
        <span
          v-if="contextFocused"
          class="rnc-body__context-counter"
          aria-live="polite"
        >
          {{ context.length }}/{{ CONTEXT_MAX }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import { LD_FLAG_IDEA_MDP_617 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";
import { suggestContextRoleStream } from "@/api/suggestContextRoleApi";
import { suggestContextResponsibilityStream } from "@/api/suggestContextResponsibilityApi";

const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  context: {
    type: String,
    default: "",
  },
  existingNames: {
    type: Array,
    default: () => [],
  },
  errorMessage: {
    type: String,
    default: "",
  },
  // The Role's own current name, which must never be treated as a duplicate.
  excludedName: {
    type: String,
    default: "",
  },
  isArchived: {
    type: Boolean,
    default: false,
  },
  nameId: {
    type: String,
    default: "role-name",
  },
  contextId: {
    type: String,
    default: "role-context",
  },
  nameLabel: {
    type: String,
    default: "",
  },
  namePlaceholder: {
    type: String,
    default: "",
  },
  // "role" | "responsibility" — selects the IQ stream and the entity copy, so
  // the Role and Responsibility dialogs share this one body.
  variant: {
    type: String,
    default: "role",
    validator: (value) => ["role", "responsibility"].includes(value),
  },
  showReplacePopover: {
    type: Boolean,
    default: false,
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
});

const emit = defineEmits([
  "update:name",
  "update:context",
  "validity-change",
  "streaming-change",
  "clear-error",
  "update:showReplacePopover",
]);

const NAME_MIN = 3;
const CONTEXT_MAX = 500;

const isResponsibility = computed(() => props.variant === "responsibility");
const entityLabel = computed(() => (isResponsibility.value ? "Responsibility" : "Role"));
const entityNoun = computed(() => entityLabel.value.toLowerCase());
const resolvedNameLabel = computed(
  () => props.nameLabel || `${entityLabel.value} Name*`
);
const resolvedNamePlaceholder = computed(
  () =>
    props.namePlaceholder ||
    (isResponsibility.value ? "e.g. Fire Extinguisher Inspection" : "e.g. Welding Supervisor")
);
const contextPlaceholder = computed(
  () => `What does this ${entityNoun.value} cover? Add scope, boundaries or examples.`
);
const suggestStream = computed(() =>
  isResponsibility.value ? suggestContextResponsibilityStream : suggestContextRoleStream
);

// Banner copy (verbatim from the Figma AddRoleDialog "Suggest Context" frames).
const CONNECTING_TITLE = "Connecting to IQ";
const FAILED_TITLE = "Couldn’t write context";
const failedMessage = computed(
  () =>
    `IQ couldn’t suggest context for this ${entityNoun.value}. Try again or write it yourself.`
);

const nameFocused = ref(false);
const contextFocused = ref(false);
const flagEnabled = ref(false);
const abortController = ref(null);

// Suggestion state machine. `idle` shows the textarea; `connecting` → `streaming`
// → `stalled` replace it with IQThinkingBannerStreaming; `done` collapses the
// banner above the textarea; `failed` shows the banner Failed state.
const suggestionStatus = ref("idle");
const reasoning = ref("");
const phase = ref("");
const stalled = ref(false);

const trimmedName = computed(() => (props.name || "").trim());
const remainingChars = computed(
  () => props.maxNameCharacters - (props.name || "").length
);
const nameTooLong = computed(
  () => trimmedName.value.length > props.maxNameCharacters
);
const contextTooLong = computed(() => (props.context || "").length > CONTEXT_MAX);

const isStreaming = computed(() =>
  ["connecting", "streaming", "stalled"].includes(suggestionStatus.value)
);

const showBanner = computed(() => suggestionStatus.value !== "idle");
const showTextarea = computed(() =>
  ["idle", "done"].includes(suggestionStatus.value)
);
const showHintRow = computed(() =>
  ["idle", "done"].includes(suggestionStatus.value)
);
const bannerState = computed(() => {
  if (suggestionStatus.value === "failed") return "failed";
  if (suggestionStatus.value === "done") return "done";
  return "thinking";
});
const bannerCollapsible = computed(() =>
  ["streaming", "stalled"].includes(suggestionStatus.value)
);

const isDuplicateName = computed(() => {
  const value = trimmedName.value.toLowerCase();
  if (!value) return false;

  const excluded = String(props.excludedName || "").trim().toLowerCase();
  const inExistingNames = (props.existingNames || []).some(
    (item) => String(item).trim().toLowerCase() === value
  );
  if (inExistingNames && value !== excluded) return true;

  const serverSaysDuplicate =
    !!props.errorMessage &&
    /already exists|already in use|unique/i.test(props.errorMessage);
  return serverSaysDuplicate;
});

const nameError = computed(() => {
  if (nameTooLong.value) {
    return `${entityLabel.value} name cannot exceed ${props.maxNameCharacters} characters.`;
  }
  if (isDuplicateName.value) {
    return "This name is already in use. Enter a different name.";
  }
  return props.errorMessage || "";
});

const isNameValid = computed(() => {
  const value = trimmedName.value;
  return (
    value.length >= NAME_MIN &&
    value.length <= props.maxNameCharacters &&
    !isDuplicateName.value
  );
});

const suggestDisabled = computed(() => {
  if (isStreaming.value) return false;
  const value = trimmedName.value;
  return (
    value.length < NAME_MIN ||
    value.length > props.maxNameCharacters ||
    props.isArchived
  );
});

const suggestLabel = computed(() => {
  if (isStreaming.value) return "Stop";
  return (props.context || "").trim() ? "Re-suggest" : "Suggest";
});

const contextOptionalLabel = computed(() =>
  props.isArchived ? "(read-only)" : "(optional)"
);

const contextHint = computed(() => {
  if (props.isArchived) return `Context can't be edited on an archived ${entityLabel.value}.`;
  if ((props.context || "").trim()) return "Suggested by IQ. Review before saving.";
  if (trimmedName.value.length > 0 && trimmedName.value.length < NAME_MIN) {
    return "Enter a name (3+ characters) to enable Suggest.";
  }
  return "Optional. Helps teammates and IQ understand its scope.";
});

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onNameInput(event) {
  const next = event && event.target ? event.target.value : event;
  emit("update:name", next);
  if (props.errorMessage) emit("clear-error");
}

function onContextInput(event) {
  const next = event && event.target ? event.target.value : event;
  emit("update:context", next);
}

function onNameFocus() {
  nameFocused.value = true;
}

function onNameBlur() {
  nameFocused.value = false;
}

function onContextFocus() {
  contextFocused.value = true;
}

function onContextBlur() {
  contextFocused.value = false;
}

function setReplacePopover(value) {
  emit("update:showReplacePopover", value);
}

function handleSuggestClick() {
  if (isStreaming.value) {
    if (abortController.value) abortController.value.abort();
    return;
  }

  if ((props.context || "").trim()) {
    setReplacePopover(true);
    return;
  }

  runSuggest();
}

function handleReplace() {
  setReplacePopover(false);
  runSuggest();
}

// Advance the banner into Streaming on the first (or resumed) stream event.
// Until ER-12291 ships the real phase label, the banner defaults to
// "Writing context …" (Eric decision, 2026-10-12).
function transitionToStreaming() {
  stalled.value = false;
  if (!phase.value) phase.value = "Writing context …";
  suggestionStatus.value = "streaming";
}

async function runSuggest() {
  if (isStreaming.value) return;

  const accountId = resolveAccountId();
  if (accountId == null) {
    suggestionStatus.value = "failed";
    return;
  }

  setReplacePopover(false);
  emit("update:context", "");
  reasoning.value = "";
  phase.value = "";
  stalled.value = false;
  suggestionStatus.value = "connecting";

  const controller = new AbortController();
  abortController.value = controller;

  try {
    await suggestStream.value({
      accountId,
      name: trimmedName.value,
      signal: controller.signal,
      onChunk: (text) => {
        emit("update:context", text);
        transitionToStreaming();
      },
      onThinking: (narrative) => {
        reasoning.value = narrative;
        transitionToStreaming();
      },
      onPhase: (label) => {
        phase.value = label;
        transitionToStreaming();
      },
      onStalled: () => {
        stalled.value = true;
        suggestionStatus.value = "stalled";
      },
      onComplete: (text) => {
        emit("update:context", text);
        suggestionStatus.value = "done";
      },
      onError: () => {
        suggestionStatus.value = "failed";
      },
    });
  } catch (error) {
    // Stop (abort) keeps whatever text was already streamed via onChunk and
    // collapses the banner to Done (Eric decision, 2026-10-12).
    if (error && error.name === "AbortError") {
      suggestionStatus.value = "done";
    } else if (suggestionStatus.value !== "failed") {
      suggestionStatus.value = "failed";
    }
  } finally {
    abortController.value = null;
  }
}

watch(isNameValid, (value) => emit("validity-change", value), { immediate: true });
watch(isStreaming, (value) => emit("streaming-change", value));

onMounted(async () => {
  try {
    flagEnabled.value = await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_617, false);
  } catch {
    flagEnabled.value = false;
  }
});
</script>

<style scoped>
.rnc-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
}

.rnc-body__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rnc-body__input-wrap {
  position: relative;
}

.rnc-body__label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.rnc-body__optional {
  color: #667085;
}

.rnc-body__counter {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
  white-space: nowrap;
  pointer-events: none;
}

.rnc-body__counter--error {
  color: #d92d20;
}

.rnc-body__input {
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.rnc-body__input--with-counter {
  padding-right: 56px;
}

.rnc-body__input::placeholder {
  color: #667085;
}

.rnc-body__input:focus {
  outline: none;
  border-color: #7f56d9;
}

.rnc-body__input--error {
  border-color: #d92d20;
}

.rnc-body__error {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}

.rnc-body__context-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.rnc-body__suggest {
  position: relative;
}

/* AskIQButton ships `min-width: 200px` + `justify-space-between`; !important is
   needed to override its scoped utility styles so the Suggest button sizes to
   its label inside the dialog header row. */
.rnc-body__context-label-row :deep(.ask-iq-button) {
  min-width: auto !important;
  justify-content: flex-start !important;
}

.rnc-body__textarea-wrapper {
  width: 100%;
  min-height: 150px;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
}

.rnc-body__textarea-wrapper--error {
  border: 2px solid #d92d20;
}

.rnc-body__textarea {
  display: block;
  width: 100%;
  min-height: 124px;
  box-sizing: border-box;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
  background: transparent;
}

.rnc-body__textarea::placeholder {
  color: #667085;
}

.rnc-body__textarea:disabled {
  color: #98a2b3;
  cursor: not-allowed;
}

.rnc-body__context-hint-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.rnc-body__hint {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #667085;
}

.rnc-body__context-counter {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
  white-space: nowrap;
}

.rnc-body__replace-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.rnc-body__replace-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 21;
  width: 340px;
  box-sizing: border-box;
  padding: 20px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.rnc-body__replace-title {
  margin: 0 0 8px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.rnc-body__replace-body {
  margin: 0 0 16px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.rnc-body__replace-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.rnc-body__replace-btn {
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}

.rnc-body__replace-btn--cancel {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.rnc-body__replace-btn--confirm {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}
</style>
